import React, { useContext, useEffect, useId, useRef, useState } from "react";
import "./navbar.css";
import { ThemeContext } from "../App";
import { FaSearch, FaMoon, FaSun } from "react-icons/fa";
import { Link, NavLink } from "react-router-dom";
import { fetchSuggestions } from "../api/recipes";
import { useShoppingList } from "../utils/stores";

const excludedWords = ["beef"];

const Navbar = ({ onSearch }) => {
  const { theme, toggleTheme } = useContext(ThemeContext);
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const formRef = useRef(null);
  const listId = useId();
  const { items } = useShoppingList();
  const pending = items.filter((i) => !i.checked).length;

  const submit = (text) => {
    if (text.trim()) {
      onSearch(text);
      setQuery("");
      setSuggestions([]);
      setOpen(false);
      setActive(-1);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    submit(active >= 0 && suggestions[active] ? suggestions[active].title : query);
  };

  // Debounced search suggestions
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setSuggestions([]);
      return undefined;
    }
    let cancelled = false;
    const t = setTimeout(() => {
      fetchSuggestions(q)
        .then((list) => {
          if (cancelled) return;
          setSuggestions(
            list
              .filter((s) => !excludedWords.some((w) => s.title.toLowerCase().includes(w)))
              .slice(0, 6)
          );
          setActive(-1);
        })
        .catch(() => !cancelled && setSuggestions([]));
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [query]);

  useEffect(() => {
    const onDown = (e) => {
      if (formRef.current && !formRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const onKeyDown = (e) => {
    if (!suggestions.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActive((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === "Escape") {
      setOpen(false);
      setActive(-1);
    }
  };

  const linkClass = ({ isActive }) => `nav-item${isActive ? " active" : ""}`;
  const showList = open && suggestions.length > 0;

  return (
    <nav className={`navbar ${theme ? "dark" : ""}`} aria-label="Main">
      <div className="navbar-inner">
        {/* Left: Logo */}
        <div className="logo">
          <Link to="/" className="logo-link">
            <img src="/logo.svg" alt="" className="logo-mark" width="34" height="34" />
            <span>Recify<i>.</i></span>
          </Link>
        </div>

        {/* Center: Links */}
        <div className="nav-links">
          <NavLink to="/" end className={linkClass}>
            Home
          </NavLink>
          <NavLink to="/favorites" className={linkClass}>
            Favorites
          </NavLink>
          <NavLink to="/categories" className={linkClass}>
            Categories
          </NavLink>
          <NavLink to="/planner" className={linkClass}>
            Planner
          </NavLink>
          <NavLink to="/shopping" className={linkClass}>
            Shopping
            {pending > 0 && <span className="nav-badge" aria-label={`${pending} items to buy`}>{pending}</span>}
          </NavLink>
        </div>

        {/* Right: Search + Theme */}
        <div className="nav-actions">
          <form className="search-bar" onSubmit={handleSubmit} role="search" ref={formRef}>
            <FaSearch className="search-icon" aria-hidden="true" />
            <input
              type="text"
              placeholder="Search recipes..."
              aria-label="Search recipes"
              role="combobox"
              aria-expanded={showList}
              aria-controls={listId}
              aria-autocomplete="list"
              autoComplete="off"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              onKeyDown={onKeyDown}
            />
            <button type="submit" aria-label="Search">
              Go
            </button>

            {showList && (
              <ul className="suggest" role="listbox" id={listId}>
                {suggestions.map((s, i) => (
                  <li
                    key={s.id}
                    role="option"
                    aria-selected={i === active}
                    className={i === active ? "active" : ""}
                    onMouseEnter={() => setActive(i)}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => submit(s.title)}
                  >
                    <img src={s.image} alt="" loading="lazy" />
                    <span>{s.title}</span>
                  </li>
                ))}
              </ul>
            )}
          </form>

          <button
            className="theme-toggle"
            onClick={toggleTheme}
            aria-label={theme ? "Switch to light mode" : "Switch to dark mode"}
            title={theme ? "Light mode" : "Dark mode"}
          >
            {theme ? <FaSun /> : <FaMoon />}
          </button>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
